import { createAIDialogButton } from './AIDialogButton';
import { getAIApiEndpoint } from '../../utils';
import {
    createSeparator,
    createButton,
    createHeadingSelect,
    createFontFamilySelect,
    createFontSizeSelect,
    createLineSpacingSelect,
    ToolbarButton,
} from './RichTextToolbar';
import { createColorDropdown } from './ColorDropdown';
import { createDragHandlers } from './DragUtils';
import {
    getCurrentTable,
    getCurrentCell,
    createTable,
    addTableColumn,
    removeTableColumn,
    addTableRow,
    removeTableRow,
} from './TableUtils';

export const createEnhancedToolbar = (
    allFonts: Array<{ name: string } | string>,
    colorVariables: string[],
    contentRef: React.RefObject<HTMLDivElement>,
    templateResourceStrings?: Record<string, string>,
    siteResourceStrings?: Record<string, string>,
    authorizationToken?: string,
    data?: any
): { toolbar: HTMLDivElement; cleanup: () => void } => {
    const toolbar = document.createElement('div');
    toolbar.className = 'rich-text-editable-toolbar';
    toolbar.style.cssText = `
      position: fixed;
      z-index: 9999;
      background: white;
      border: 1px solid #ccc;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      padding: 10px;
      display: flex;
      gap: 6px;
      align-items: center;
      flex-wrap: wrap;
      max-width: 900px;
      min-width: 650px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      cursor: move;
    `;

    // Make toolbar draggable
    const { cleanup: dragCleanup } = createDragHandlers(toolbar);

    // Create heading dropdown
    const headingSelect = createHeadingSelect(contentRef);
    toolbar.appendChild(headingSelect);
    toolbar.appendChild(createSeparator());

    // Create font family dropdown
    const fontFamilySelect = createFontFamilySelect(allFonts, contentRef);
    toolbar.appendChild(fontFamilySelect);

    // Create font size dropdown
    const fontSizeSelect = createFontSizeSelect(contentRef);
    toolbar.appendChild(fontSizeSelect);

    // Create line spacing dropdown
    const lineSpacingSelect = createLineSpacingSelect(contentRef);
    toolbar.appendChild(lineSpacingSelect);
    toolbar.appendChild(createSeparator());

    // Function to update all dropdown values based on current cursor position
    const updateToolbarState = () => {
        if (!contentRef.current) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        let currentNode: Node | null = range.startContainer;

        // Reset values
        headingSelect.value = 'div';
        fontFamilySelect.value = '';
        fontSizeSelect.value = '';
        lineSpacingSelect.value = '';

        // Walk up the DOM tree to find styles
        while (currentNode && currentNode !== contentRef.current) {
            if (currentNode.nodeType === Node.ELEMENT_NODE) {
                const element = currentNode as Element;
                const tagName = element.tagName.toLowerCase();
                const computedStyle = window.getComputedStyle(element);

                // Update heading
                if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
                    headingSelect.value = tagName;
                }

                // Update font family
                const fontFamily = computedStyle.fontFamily;
                if (fontFamily && fontFamily !== 'inherit' && fontFamilySelect.value === '') {
                    const cleanFontFamily = fontFamily.replace(/['"]/g, '').split(',')[0].trim();
                    for (const font of allFonts) {
                        const fontName = typeof font === 'string' ? font : font.name || '';
                        if (fontName === cleanFontFamily || fontFamily.includes(fontName)) {
                            fontFamilySelect.value = fontName;
                            break;
                        }
                    }
                }

                // Update font size
                const fontSize = computedStyle.fontSize;
                if (fontSize && fontSize !== 'inherit' && fontSizeSelect.value === '') {
                    const sizeMatch = fontSize.match(/(\d+(?:\.\d+)?)px/);
                    if (sizeMatch) {
                        const currentSize = Math.round(parseFloat(sizeMatch[1]));
                        const sizes = [
                            8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72,
                        ];
                        if (sizes.includes(currentSize)) {
                            fontSizeSelect.value = currentSize.toString();
                        }
                    }
                }

                // Update line spacing
                const lineHeight = computedStyle.lineHeight;
                if (lineHeight && lineHeight !== 'inherit' && lineSpacingSelect.value === '') {
                    let numericValue: number;

                    // Handle both numeric values and 'normal'
                    if (lineHeight === 'normal') {
                        numericValue = 1.2; // Browser default for normal
                    } else if (lineHeight.endsWith('px')) {
                        // Convert px to relative value by dividing by font size
                        const fontSize = parseFloat(computedStyle.fontSize);
                        const lineHeightPx = parseFloat(lineHeight);
                        numericValue = lineHeightPx / fontSize;
                    } else {
                        // Direct numeric value (unitless or em)
                        numericValue = parseFloat(lineHeight);
                    }

                    if (!isNaN(numericValue)) {
                        // Check if it matches one of our predefined values (with tolerance)
                        const lineHeights = ['1', '1.15', '1.5', '2', '2.5', '3'];
                        const closest = lineHeights.find(
                            (value) => Math.abs(parseFloat(value) - numericValue) < 0.15
                        );
                        if (closest) {
                            lineSpacingSelect.value = closest;
                        }
                    }
                }
            }
            currentNode = currentNode.parentNode;
        }

        // Update button active states for formatting commands
        const formatButtons = toolbar.querySelectorAll('button[data-command]');
        formatButtons.forEach((button) => {
            const buttonElement = button as HTMLButtonElement;
            if ((buttonElement as any).updateActiveState) {
                (buttonElement as any).updateActiveState();
            }
        });
    };

    // Keep track of event listeners for cleanup
    let cleanupFunctions: (() => void)[] = [];

    // Set up event listeners to update toolbar state
    const setupToolbarUpdateListeners = () => {
        if (!contentRef.current) return;

        const events = ['click', 'keyup'];
        events.forEach((eventType) => {
            const handler = updateToolbarState;
            contentRef.current?.addEventListener(eventType, handler);
            cleanupFunctions.push(() => {
                contentRef.current?.removeEventListener(eventType, handler);
            });
        });

        // selectionchange needs to be on document
        const selectionHandler = updateToolbarState;
        document.addEventListener('selectionchange', selectionHandler);
        cleanupFunctions.push(() => {
            document.removeEventListener('selectionchange', selectionHandler);
        });

        // Initial update
        setTimeout(updateToolbarState, 100);
    };

    // Set up listeners after a brief delay to ensure contentRef is ready
    setTimeout(setupToolbarUpdateListeners, 50);

    // Basic formatting buttons
    const basicButtons: ToolbarButton[] = [
        { command: 'bold', label: '<strong>B</strong>', title: 'Bold' },
        { command: 'italic', label: '<em>I</em>', title: 'Italic' },
        { command: 'underline', label: '<u>U</u>', title: 'Underline' },
        { command: 'strikethrough', label: '<s>S</s>', title: 'Strikethrough' },
        { command: 'subscript', label: '', title: 'Subscript', className: 'fas fa-subscript' },
        {
            command: 'superscript',
            label: '',
            title: 'Superscript',
            className: 'fas fa-superscript',
        },
    ];

    basicButtons.forEach((btn) => {
        toolbar.appendChild(createButton(btn, contentRef));
    });

    // Add hyperlink button
    const linkButton = document.createElement('button');
    linkButton.innerHTML = '<i class="fas fa-link"></i>';
    linkButton.title = 'Insert Link';
    linkButton.style.cssText = `
        padding: 8px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 4px;
    `;

    linkButton.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (contentRef.current) {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const selectedText = selection.toString();
                const url = prompt('Enter URL:', 'https://');
                if (url) {
                    // Ask for link text
                    const linkText = prompt(
                        'Enter link text (leave empty to use URL):',
                        selectedText || url
                    );
                    const finalLinkText = linkText && linkText.trim() !== '' ? linkText : url;

                    if (selectedText) {
                        // If text was selected, replace it with the link
                        document.execCommand('createLink', false, url);
                        // Update the link text if different from selected text
                        if (linkText && linkText.trim() !== '' && linkText !== selectedText) {
                            const range = selection.getRangeAt(0);
                            const linkElement = range.startContainer.parentElement;
                            if (linkElement && linkElement.tagName.toLowerCase() === 'a') {
                                linkElement.textContent = finalLinkText;
                            }
                        }
                    } else {
                        // If no text selected, insert new link
                        const range = selection.getRangeAt(0);
                        const link = document.createElement('a');
                        link.href = url;
                        link.textContent = finalLinkText;
                        range.insertNode(link);

                        // Move cursor after the link
                        range.setStartAfter(link);
                        range.collapse(true);
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }
                }
            }
        }
    });

    toolbar.appendChild(linkButton);
    toolbar.appendChild(createSeparator());

    // Color dropdowns
    const textColorDropdown = createColorDropdown(
        'Text Color',
        'foreColor',
        colorVariables,
        contentRef
    );
    toolbar.appendChild(textColorDropdown);
    // Add cleanup for text color dropdown
    if ((textColorDropdown as any)._cleanup) {
        cleanupFunctions.push((textColorDropdown as any)._cleanup);
    }

    const bgColorDropdown = createColorDropdown(
        'Background Color',
        'backColor',
        colorVariables,
        contentRef
    );
    toolbar.appendChild(bgColorDropdown);
    // Add cleanup for background color dropdown
    if ((bgColorDropdown as any)._cleanup) {
        cleanupFunctions.push((bgColorDropdown as any)._cleanup);
    }
    toolbar.appendChild(createSeparator());

    // List and alignment buttons
    const listButtons: ToolbarButton[] = [
        {
            command: 'insertUnorderedList',
            label: '',
            title: 'Bullet List',
            className: 'fas fa-list-ul',
        },
        {
            command: 'insertOrderedList',
            label: '',
            title: 'Ordered List',
            className: 'fas fa-list-ol',
        },
    ];

    listButtons.forEach((btn) => {
        toolbar.appendChild(createButton(btn, contentRef));
    });
    toolbar.appendChild(createSeparator());

    const alignButtons: ToolbarButton[] = [
        { command: 'justifyLeft', label: '', title: 'Align Left', className: 'fas fa-align-left' },
        {
            command: 'justifyCenter',
            label: '',
            title: 'Align Center',
            className: 'fas fa-align-center',
        },
        {
            command: 'justifyRight',
            label: '',
            title: 'Align Right',
            className: 'fas fa-align-right',
        },
    ];

    alignButtons.forEach((btn) => {
        toolbar.appendChild(createButton(btn, contentRef));
    });
    toolbar.appendChild(createSeparator());

    // Undo/Redo buttons
    const historyButtons: ToolbarButton[] = [
        { command: 'undo', label: '↶', title: 'Undo' },
        { command: 'redo', label: '↷', title: 'Redo' },
    ];

    historyButtons.forEach((btn) => {
        toolbar.appendChild(createButton(btn, contentRef));
    });

    toolbar.appendChild(createSeparator());

    // Add image button
    const imageButton = document.createElement('button');
    imageButton.innerHTML = '<i class="fas fa-image"></i>';
    imageButton.title = 'Insert Image';
    imageButton.style.cssText = `
        padding: 8px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 4px;
    `;

    imageButton.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (contentRef.current) {
            const imagePath = prompt('Enter image path:');
            if (imagePath) {
                const selection = window.getSelection();
                if (selection && selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    const img = document.createElement('img');
                    img.src = imagePath;
                    img.style.maxWidth = '100%';
                    img.style.height = 'auto';
                    range.insertNode(img);

                    // Move cursor after the image
                    range.setStartAfter(img);
                    range.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            }
        }
    });

    toolbar.appendChild(imageButton);
    toolbar.appendChild(createSeparator());

    // Add table functionality

    // Insert table button
    const tableButton = document.createElement('button');
    tableButton.innerHTML = '<i class="fas fa-table"></i>';
    tableButton.title = 'Insert Table';
    tableButton.style.cssText = `
        padding: 8px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 4px;
    `;

    tableButton.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (contentRef.current) {
            const rows = prompt('Number of rows:', '3');
            const cols = prompt('Number of columns:', '3');

            if (rows && cols) {
                const numRows = parseInt(rows);
                const numCols = parseInt(cols);

                if (numRows > 0 && numCols > 0) {
                    const selection = window.getSelection();
                    if (selection && selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);

                        const table = createTable(numRows, numCols);
                        range.insertNode(table);

                        // Move cursor after the table
                        range.setStartAfter(table);
                        range.collapse(true);
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }
                }
            }
        }
    });

    toolbar.appendChild(tableButton);

    // Add column button
    const addColumnButton = document.createElement('button');
    addColumnButton.innerHTML = '<i class="fas fa-plus"></i> Col';
    addColumnButton.title = 'Add Column';
    addColumnButton.style.cssText = `
        padding: 8px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 4px;
    `;

    addColumnButton.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const currentCell = getCurrentCell(contentRef);
        const currentTable = getCurrentTable(contentRef);

        if (currentCell && currentTable) {
            addTableColumn(currentCell, currentTable);
        }
    });

    toolbar.appendChild(addColumnButton);

    // Remove column button
    const removeColumnButton = document.createElement('button');
    removeColumnButton.innerHTML = '<i class="fas fa-minus"></i> Col';
    removeColumnButton.title = 'Remove Column';
    removeColumnButton.style.cssText = `
        padding: 8px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 4px;
    `;

    removeColumnButton.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const currentCell = getCurrentCell(contentRef);
        const currentTable = getCurrentTable(contentRef);

        if (currentCell && currentTable) {
            removeTableColumn(currentCell, currentTable);
        }
    });

    toolbar.appendChild(removeColumnButton);

    // Add row button
    const addRowButton = document.createElement('button');
    addRowButton.innerHTML = '<i class="fas fa-plus"></i> Row';
    addRowButton.title = 'Add Row';
    addRowButton.style.cssText = `
        padding: 8px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 4px;
    `;

    addRowButton.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const currentCell = getCurrentCell(contentRef);
        const currentTable = getCurrentTable(contentRef);

        if (currentCell && currentTable) {
            addTableRow(currentCell);
        }
    });

    toolbar.appendChild(addRowButton);

    // Remove row button
    const removeRowButton = document.createElement('button');
    removeRowButton.innerHTML = '<i class="fas fa-minus"></i> Row';
    removeRowButton.title = 'Remove Row';
    removeRowButton.style.cssText = `
        padding: 8px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 4px;
    `;

    removeRowButton.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const currentCell = getCurrentCell(contentRef);
        const currentTable = getCurrentTable(contentRef);

        if (currentCell && currentTable) {
            removeTableRow(currentCell);
        }
    });

    toolbar.appendChild(removeRowButton);
    toolbar.appendChild(createSeparator());

    // Add HTML/WYSIWYG toggle button
    let isHtmlMode = false;
    let originalContent = '';

    const toggleButton = document.createElement('button');
    toggleButton.innerHTML = 'HTML <i class="fas fa-toggle-off"></i>';
    toggleButton.title = 'HTML off';
    toggleButton.style.cssText = `
        padding: 8px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 4px;
    `;

    const updateToggleButton = () => {
        if (isHtmlMode) {
            toggleButton.innerHTML = 'HTML <i class="fas fa-toggle-on"></i>';
            toggleButton.style.backgroundColor = '#007bff';
            toggleButton.style.color = 'white';
            toggleButton.style.borderColor = '#007bff';
        } else {
            toggleButton.innerHTML = 'HTML <i class="fas fa-toggle-off"></i>';
            toggleButton.style.backgroundColor = 'white';
            toggleButton.style.color = 'inherit';
            toggleButton.style.borderColor = '#ddd';
        }
    };

    toggleButton.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (contentRef.current) {
            if (!isHtmlMode) {
                // Switch to HTML mode
                originalContent = contentRef.current.innerHTML;
                contentRef.current.textContent = originalContent;
                contentRef.current.style.fontFamily = 'monospace';
                contentRef.current.style.fontSize = '12px';
                contentRef.current.style.whiteSpace = 'pre-wrap';
                isHtmlMode = true;
            } else {
                // Switch back to WYSIWYG mode
                const htmlContent = contentRef.current.textContent || '';
                contentRef.current.innerHTML = htmlContent;
                contentRef.current.style.fontFamily = '';
                contentRef.current.style.fontSize = '';
                contentRef.current.style.whiteSpace = '';
                isHtmlMode = false;
            }
            updateToggleButton();
        }
    });

    toolbar.appendChild(toggleButton);

    // Add data string button
    const dataStringButton = document.createElement('button');
    dataStringButton.innerHTML = '<i class="fas fa-database"></i>';
    dataStringButton.title = 'Insert Data Property';
    dataStringButton.style.cssText = `
        padding: 8px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 4px;
    `;

    dataStringButton.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (contentRef.current) {
            const propertyName = prompt(
                'Enter data property name (e.g., "profileId", "fund.name"):',
                ''
            );
            if (propertyName && propertyName.trim() !== '') {
                const selection = window.getSelection();
                if (selection && selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);

                    // Create a span element with the data property placeholder
                    const dataSpan = document.createElement('span');
                    dataSpan.setAttribute('data-property-key', propertyName.trim());
                    dataSpan.textContent = `{{${propertyName.trim()}}}`;
                    dataSpan.style.cssText = `
                        background-color: #e6f3ff;
                        border: 1px dashed #0066cc;
                        padding: 2px 4px;
                        border-radius: 3px;
                        display: inline-block;
                        margin: 0 2px;
                    `;

                    // Insert the data span at cursor position
                    range.insertNode(dataSpan);

                    // Move cursor after the span
                    range.setStartAfter(dataSpan);
                    range.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            }
        }
    });

    toolbar.appendChild(dataStringButton);
    toolbar.appendChild(createSeparator());

    // Add resource strings dropdown if available
    const hasTemplateResources =
        templateResourceStrings && Object.keys(templateResourceStrings).length > 0;
    const hasSiteResources = siteResourceStrings && Object.keys(siteResourceStrings).length > 0;

    if (hasTemplateResources || hasSiteResources) {
        toolbar.appendChild(createSeparator());

        const resourceDropdown = document.createElement('select');
        resourceDropdown.style.cssText = `
            padding: 6px 10px;
            border: 1px solid #ddd;
            border-radius: 4px;
            background: white;
            cursor: pointer;
            font-size: 14px;
            min-width: 140px;
            max-width: 200px;
        `;

        // Add default option
        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = 'Resource Strings';
        defaultOption.disabled = true;
        defaultOption.selected = true;
        resourceDropdown.appendChild(defaultOption);

        // Helper function to create sorted options
        const createSortedOptions = (resources: Record<string, string>, prefix: string = '') => {
            return Object.keys(resources)
                .sort() // Sort alphabetically by key
                .map((key) => ({
                    value: key,
                    text: `${prefix}${key}: ${resources[key]}`,
                }));
        };

        // Add template-level resource strings first (if any)
        if (hasTemplateResources) {
            const templateOptions = createSortedOptions(templateResourceStrings!, '');
            templateOptions.forEach(({ value, text }) => {
                const option = document.createElement('option');
                option.value = value;
                option.textContent = text;
                resourceDropdown.appendChild(option);
            });
        }

        // Add divider if both template and site resources exist
        if (hasTemplateResources && hasSiteResources) {
            const divider = document.createElement('option');
            divider.disabled = true;
            divider.textContent = '──────────────────';
            resourceDropdown.appendChild(divider);
        }

        // Add site-level resource strings (if any)
        if (hasSiteResources) {
            const siteOptions = createSortedOptions(siteResourceStrings!, '');
            siteOptions.forEach(({ value, text }) => {
                const option = document.createElement('option');
                option.value = value;
                option.textContent = text;
                option.style.color = '#666'; // Slightly grayed out to distinguish from template resources
                resourceDropdown.appendChild(option);
            });
        }

        // Merge all resources for lookup during insertion
        const allResourceStrings = { ...siteResourceStrings, ...templateResourceStrings };

        resourceDropdown.addEventListener('change', (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (resourceDropdown.value && contentRef.current) {
                // Focus back to the editor to maintain the selection
                contentRef.current.focus();

                const selection = window.getSelection();
                if (selection && selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);

                    // Create a span element with the resource string template
                    const resourceSpan = document.createElement('span');
                    resourceSpan.setAttribute('data-resource-key', resourceDropdown.value);
                    resourceSpan.textContent =
                        allResourceStrings[resourceDropdown.value] || resourceDropdown.value;
                    resourceSpan.style.cssText = `
                        background-color: #e7f3ff;
                        border: 1px dashed #007cba;
                        padding: 2px 4px;
                        border-radius: 3px;
                        display: inline-block;
                        margin: 0 2px;
                    `;

                    // Insert the resource span at cursor position
                    range.insertNode(resourceSpan);

                    // Move cursor after the span
                    range.setStartAfter(resourceSpan);
                    range.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }

                // Reset dropdown to default state
                setTimeout(() => {
                    resourceDropdown.value = '';
                }, 100);
            }
        });

        toolbar.appendChild(resourceDropdown);

        // AI dialog button
        toolbar.appendChild(createSeparator());
        const aiDialogButton = createAIDialogButton(
            contentRef,
            getAIApiEndpoint(),
            authorizationToken,
            data
        );

        toolbar.appendChild(aiDialogButton);
        if ((aiDialogButton as any)._cleanup) {
            cleanupFunctions.push((aiDialogButton as any)._cleanup);
        }
    }

    // Create cleanup function
    const cleanup = () => {
        cleanupFunctions.forEach((fn) => fn());
        cleanupFunctions = [];

        // Clean up drag event listeners
        dragCleanup();
    };

    return { toolbar, cleanup };
};
